import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { tap, throttleTime } from 'rxjs/operators';

import { PaidFeature, UserRoleType } from '@vi-iot/client';

import { AuthActions } from './auth.actions';
import { AuthSelectors } from './auth.selectors';
import { MeUserDetails } from './auth.state';
import { OAuthService } from 'angular-oauth2-oidc';

@Injectable({ providedIn: 'root' })
export class AuthStore {
  userId$ = this.store.select(AuthSelectors.getUserId());
  state$ = this.store.select(AuthSelectors.getState());
  isAuthenticating$ = this.store.select(AuthSelectors.selectIsAuthenticating);
  authenticated$ = this.store.select(AuthSelectors.selectAuthenticatedState());
  me$ = this.store.select(AuthSelectors.getMe());
  email$ = this.store.select(AuthSelectors.getEmail());
  loginId$ = this.store.select(AuthSelectors.getLoginId());
  isMaintainerOrUser$ = this.store.select(AuthSelectors.isMaintainerOrUser());
  isMaintainer$ = this.store.select(AuthSelectors.isMaintainer);
  isEndUser$ = this.store.select(AuthSelectors.isEndUser());
  isLiteAccount$ = this.store.select(AuthSelectors.isLiteAccount());
  isRequiredApplicationRole$ = this.store.select(AuthSelectors.isRequiredApplicationRole());
  isCommercialUser$ = this.store.select(AuthSelectors.isCommercialUser);
  isAccessFeedbackApplicationRole$ = this.store.select(
    AuthSelectors.selectIsAccessFeedbackApplicationRole()
  );
  isRsaXotaApplicationRole$ = this.store.select(AuthSelectors.isRsaXotaApplicationRole());
  selectDashboardFeatureIfAvailable$ = this.store.select(
    AuthSelectors.selectDashboardFeatureIfAvailable
  );
  isUserWithRoleInvitation$ = this.store.select(AuthSelectors.isUserWithRoleInvitation);
  isUserOfCommercialOwnerCompanyWithRoleInvitation$ = this.store.select(
    AuthSelectors.isUserOfCommercialOwnerCompanyWithRoleInvitation
  );
  isUserWithHeatingSystemsOverviewRole$ = this.store.select(
    AuthSelectors.selectIsUserWithHeatingSystemsOverviewRole
  );
  isUserNavigateToHeatingSystemsOverviewExternal$ = this.store.select(
    AuthSelectors.selectIsUserNavigateToHeatingSystemsOverviewExternal
  );
  companyName$ = this.store.select(AuthSelectors.getCompanyName());
  canManageSubscriptions$ = this.store.select(AuthSelectors.userCanManageSubscriptions());

  analyticsClientGroup$ = this.store.select(AuthSelectors.analyticsClientGroup());
  hasAccessToDiagnostics$ = this.store.select(AuthSelectors.hasAccessToDiagnostics());
  hasAccessToNotificationSettings$ = this.store.select(
    AuthSelectors.userHasAccessToNotificationSettings()
  );
  isCompanyFromUsaOrCanada$ = this.store.select(AuthSelectors.isCompanyFromUsaOrCanada());
  isCompanyFromGermany$ = this.store.select(AuthSelectors.isCompanyFromGermany());
  isCompanyFromNetherlandsOrPoland$ = this.store.select(
    AuthSelectors.isCompanyFromNetherlandsOrPoland()
  );
  canEditBmuSerial$ = this.store.select(
    AuthSelectors.canEditBmuSerial([
      UserRoleType.admin,
      UserRoleType.supportInternational,
      UserRoleType.supportInternationalWithRemoteDiagnosis,
    ])
  );
  companyAddressAsString$ = this.store.select(AuthSelectors.getCompanyAddressAsString);
  getCompanyIdForInstallationsListInit$ = this.store.select(
    AuthSelectors.getCompanyIdForInstallationsListInit
  );

  userAddressCountryCode$ = this.store.select(AuthSelectors.getAddressCountryCode());

  userLanguageCode$ = this.store.select(AuthSelectors.getLanguageCode());

  fetchCSRFTokenOn401Subject$ = new Subject<void>();
  isDemo$: Observable<boolean> = this.store.select(AuthSelectors.isDemo).pipe(
    tap((value) => {
      this.isDemoSync = value;
    })
  );
  isDemoSync = false;
  isAuthorized$ = this.store.select(AuthSelectors.selectAuthorized);
  isAuthorizing$ = this.store.select(AuthSelectors.selectIsAuthorizing);
  serviceTicketAccess$ = this.store.select(AuthSelectors.selectServiceTicketAccess());
  companySubscriptionPlan$ = this.store.select(AuthSelectors.selectCompanySubscriptionPlan());
  connectivityRankingPositions$ = this.store.select(
    AuthSelectors.selectConnectivityRankingPositions
  );
  isEndUserWithoutAccess$ = this.store.select(AuthSelectors.selectIsEndUserWithoutAccess);
  dashboardUserType$ = this.store.select(AuthSelectors.selectDashboardUserType);
  isCompanyFromGermanyOrAustria$ = this.store.select(
    AuthSelectors.selectIsCompanyFromGermanyOrAustria()
  );
  primaryVkorg$ = this.store.select(AuthSelectors.selectPrimaryVkorg);

  constructor(private store: Store, private oAuthService: OAuthService) {
    this.subscribeToFetchCSRFTokenOn401Subject();
  }

  startAuthFlow(): void {
    this.store.dispatch(AuthActions.startAuthFlow({ repeatedAfter401: false }));
  }

  startLogin(): void {
    this.store.dispatch(AuthActions.authenticateMe.attempt());
  }

  logout(): void {
    this.store.dispatch(AuthActions.logout());
  }

  updateEmail(email: string): void {
    this.store.dispatch(AuthActions.updateEmail({ email }));
  }

  updatePhone(phone: string): void {
    this.store.dispatch(AuthActions.updatePhone({ phone }));
  }

  runDemo(): void {
    this.store.dispatch(AuthActions.runDemo());
  }

  exitDemo(): void {
    this.store.dispatch(AuthActions.exitDemo());
  }

  showLearningRoomBanner(): void {
    this.store.dispatch(AuthActions.learningRoomBanner.showAttempt());
  }

  closeLearningRoomBanner(): void {
    this.store.dispatch(AuthActions.learningRoomBanner.closeAttempt());
  }

  showEventBanner(): void {
    this.store.dispatch(AuthActions.eventBanner.showAttempt());
  }

  closeEventBanner(): void {
    this.store.dispatch(AuthActions.eventBanner.closeAttempt());
  }

  showPaidFeaturesBanner(): void {
    this.store.dispatch(AuthActions.paidFeaturesBanner.showAttempt());
  }

  closePaidFeaturesBanner(): void {
    this.store.dispatch(AuthActions.paidFeaturesBanner.closeAttempt());
  }

  iframeRefreshToken(): void {
    this.store.dispatch(AuthActions.iframeRefreshToken());
  }

  isAdminOrSupportInternational(): Observable<boolean> {
    return this.store.select(AuthSelectors.isAdminOrSupportInternational());
  }

  isRsaUser(): Observable<boolean> {
    return this.store.select(AuthSelectors.isRsaUser());
  }

  isRsaUserWithWriteRights(): Observable<boolean> {
    return this.store.select(AuthSelectors.isRsaUserWithWriteRights());
  }

  getMe(): Observable<MeUserDetails> {
    return this.store.select(AuthSelectors.getMe());
  }

  getName(): Observable<string> {
    return this.store.select(AuthSelectors.getName());
  }

  getPhone(): Observable<string> {
    return this.store.select(AuthSelectors.getPhone());
  }

  getVkorg(): Observable<string> {
    return this.store.select(AuthSelectors.getVkorg());
  }

  getCountryCode(): Observable<string> {
    return this.store.select(AuthSelectors.getCountryCode());
  }

  getCompanyName(): Observable<string> {
    return this.store.select(AuthSelectors.getCompanyName());
  }

  getCompanyId(): Observable<string> {
    return this.store.select(AuthSelectors.getCompanyId());
  }

  userHasAccessToInvitations(): Observable<boolean> {
    return this.store.select(AuthSelectors.userHasAccessToInvitations());
  }

  getUserRoles(): Observable<string[]> {
    return this.store.select(AuthSelectors.getUserRoles());
  }

  getUserIotRole(): Observable<UserRoleType> {
    return this.store.select(AuthSelectors.getUserIotRole());
  }

  hasOneOfUserIotRoles(userIotRoles: UserRoleType | UserRoleType[]): Observable<boolean> {
    return this.store.select(AuthSelectors.hasOneOfUserIotRoles(userIotRoles));
  }

  isLearningRoomVisible(): Observable<boolean> {
    return this.store.select(AuthSelectors.isLearningRoomVisible());
  }

  isNewVitoguideFeaturesBannerVisible(): Observable<boolean> {
    return this.store.select(AuthSelectors.isNewVitoguideFeaturesBannerVisible());
  }

  isPaidFeaturesBannerVisible(): Observable<boolean> {
    return this.store.select(AuthSelectors.isPaidFeaturesBannerVisible());
  }

  getIdentityTypeValue(): Observable<string> {
    return this.store.select(AuthSelectors.getIdentityTypeValue());
  }

  checkAvailableGlobalPaidFeaturePresence(feature: PaidFeature): Observable<boolean> {
    return this.store.select(AuthSelectors.selectAvailableGlobalPaidFeaturePresence(feature));
  }

  getToken(): string {
    return this.oAuthService.getAccessToken();
  }

  private subscribeToFetchCSRFTokenOn401Subject(): void {
    this.fetchCSRFTokenOn401Subject$.pipe(throttleTime(10000)).subscribe(() => {
      this.store.dispatch(AuthActions.refreshTokenOn401Error());
    });
  }
}
